// ABOUTME: Keeps the "Next Responsibility" card on the just-completed chore list for a grace
// ABOUTME: period instead of swapping instantly to the next incomplete list (issue #1916)

import { useEffect, useRef, useState } from 'react';
import { ChoreListPeriodStatus } from '../types/api';
import { pickNextChoreList, CHORE_LIST_DELAY_MS } from '../utils/choreListUtils';

function msSinceCompleted(choreList: ChoreListPeriodStatus): number {
  if (!choreList.completedAt) return Infinity; // no timestamp — treat as already elapsed
  return Date.now() - new Date(choreList.completedAt).getTime();
}

/**
 * Returns the same list pickNextChoreList would (the first incomplete list, in array order),
 * except when the currently-shown list just transitioned to complete: that list keeps showing
 * until CHORE_LIST_DELAY_MS has elapsed since its completedAt timestamp (wall-clock, mirroring
 * useDelayedChoreListView's createdAt anchor — a completion from 4s ago only waits the remaining
 * ~1s even on a fresh mount) before swapping to the fresh pick. If the shown list disappears
 * entirely, falls back to the fresh pick immediately.
 */
export function useFrozenNextChoreList(choreStatuses: ChoreListPeriodStatus[]): ChoreListPeriodStatus | undefined {
  // frozenId is React state (not a ref) so it's safe to read during render — a ref's `.current`
  // must only be touched in effects/handlers, never in the render body or return value.
  const [frozenId, setFrozenId] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const freshPick = pickNextChoreList(choreStatuses);

  useEffect(() => {
    const frozenList = frozenId != null ? choreStatuses.find(l => l.id === frozenId) : undefined;

    if (frozenId == null || !frozenList || !frozenList.isComplete) {
      // Nothing frozen, the frozen list vanished, or it's still incomplete — no countdown needed;
      // adopt the fresh pick as the frozen id so future completions are detected as transitions.
      setFrozenId(freshPick?.id ?? null);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const elapsed = msSinceCompleted(frozenList);
    if (elapsed >= CHORE_LIST_DELAY_MS) {
      setFrozenId(freshPick?.id ?? null);
      return;
    }

    if (!timerRef.current) {
      const remaining = CHORE_LIST_DELAY_MS - elapsed;
      timerRef.current = setTimeout(() => {
        setFrozenId(null);
        timerRef.current = null;
      }, remaining);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choreStatuses]);

  // Check elapsed time directly (not just frozenId) so a list whose grace period has already
  // passed swaps away on the very first render, before the effect above has had a chance to run.
  if (frozenId != null) {
    const frozenList = choreStatuses.find(l => l.id === frozenId);
    if (frozenList && (!frozenList.isComplete || msSinceCompleted(frozenList) < CHORE_LIST_DELAY_MS)) {
      return frozenList;
    }
  }
  return freshPick;
}
